import db from '../../../../services/firebase'
import {collection, getDocs, getDoc, addDoc, updateDoc, deleteDoc, doc, onSnapshot, serverTimestamp} from 'firebase/firestore'

class AssetDataService {
  addAsset = async (newAsset, currentUser) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetRef = collection(tenantRef, 'assets')
      
      const assetDoc = await addDoc(assetRef, {
        ...newAsset,
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
      })

      await this.logActivity(currentUser, `added a new asset '${newAsset.name}'`, 'Asset added', 'Asset Management')

      return assetDoc
    } catch (error) {
      console.error('Error adding asset:', error)
      throw error
    }
  }

  updateAsset = async (id, updateAsset, currentUser) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetRef = doc(tenantRef, 'assets', id)
      
      await updateDoc(assetRef, updateAsset)

      await this.logActivity(currentUser, `updated asset '${updateAsset.name}'`, 'Asset updated', 'Asset Management')

      return true
    } catch (error) {
      console.error('Error updating asset:', error)
      throw error
    }
  }

  deleteAsset = async (id, assetName, currentUser) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetRef = doc(tenantRef, 'assets', id)
      
      await deleteDoc(assetRef)

      await this.logActivity(currentUser, `deleted asset '${assetName}'`, 'Asset deleted', 'Asset Management')

      return true
    } catch (error) {
      console.error('Error deleting asset:', error)
      throw error
    }
  }

  getAssets = (currentUser, setAssets, setShowEmptyState) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetRef = collection(tenantRef, 'assets')

      const unsubscribe = onSnapshot(assetRef, (snapshot) => {
        const assetData = snapshot.docs.map((item) => ({
          ...item.data(),
          id: item.id,
          is_repo: item.data().is_repo,
        })).filter((data) => data.is_repo !== true && data.type !== 'domain')
        setAssets(assetData)
        setShowEmptyState(false)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting assets:', error)
      setShowEmptyState(false)
    }
  }

  getAssetById = async (id, currentUser) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetRef = doc(tenantRef, 'assets', id)
      
      const assetDoc = await getDoc(assetRef)
      if (assetDoc.exists()) {
        const data = assetDoc.data()
        return {
          id: assetDoc.id,
          serial_number: data.serial_number || '',
          owner: data.owner || '',
          model: data.model || '',
          name: data.name || '',
          os_version: data.os_version || '',
          last_checkedin: data.last_checkedin || '',
          anti_virus: data.anti_virus || 'No',
          hd_encryption: data.hd_encryption || 'No',
          password_policy: data.password_policy || 'No',
          screen_policy: data.screen_policy || 'No',
          firewall: data.firewall || 'No',
          audit_scope: data.audit_scope || 'No',
          ...data
        }
      }
      return null
    } catch (error) {
      console.error('Error getting asset by ID:', error)
      throw error
    }
  }

  getRepositories = (currentUser, setRepositories) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetRef = collection(tenantRef, 'assets')

      const unsubscribe = onSnapshot(assetRef, (snapshot) => {
        const repoData = snapshot.docs
          .map((item) => {
            const data = item.data()
            return {
              ...data,
              id: item.id,
              is_repo: data.is_repo,
            }
          })
          .filter((data) => data.is_repo === true)
        setRepositories(repoData)
      })
      return unsubscribe
    } catch (error) {
      console.error('Error getting repositories:', error)
    }
  }

  updateRepositoryAuditScope = async (repositoryIds, newScopeValue, currentUser) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)

      for (const repoId of repositoryIds) {
        const assetRef = doc(tenantRef, 'assets', repoId)
        await updateDoc(assetRef, {
          audit_scope: newScopeValue,
        })
      }

      await this.logActivity(
        currentUser, 
        `marked ${repositoryIds.length} repository(ies) ${newScopeValue ? 'in' : 'out of'} audit scope`, 
        'Repository scope updated', 
        'Asset Management'
      )

      return true
    } catch (error) {
      console.error('Error updating repository audit scope:', error)
      throw error
    }
  }

  assignRepositoryOwner = async (repositoryIds, ownerName, currentUser) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)

      for (const repoId of repositoryIds) {
        const assetRef = doc(tenantRef, 'assets', repoId)
        await updateDoc(assetRef, {
          integration_owner: ownerName,
        })
      }
      await this.logActivity(
        currentUser, 
        `assigned owner '${ownerName}' to ${repositoryIds.length} repository(ies)`, 
        'Repository owner assigned', 
        'Asset Management'
      )

      return true
    } catch (error) {
      console.error('Error assigning repository owner:', error)
      throw error
    }
  }

  addAssetsFromCSV = async (parsedDataArray, currentUser, fileName) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetsRef = collection(tenantRef, 'assets')

      for (const parsedData of parsedDataArray) {
        await addDoc(assetsRef, {
          ...parsedData,
          uploadedBy: currentUser.uid,
          uploadedAt: serverTimestamp(),
        })
      }

      await this.logActivity(
        currentUser, 
        `uploaded ${parsedDataArray.length} asset(s) through CSV '${fileName}'`, 
        'Assets added via CSV', 
        'Asset Management'
      )

      return true
    } catch (error) {
      console.error('Error adding assets from CSV:', error)
      throw error
    }
  }

  getEmployees = (currentUser, setEmployees) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) {
        return
      }
      const tenantRef = doc(db, 'tenants', tenantId)
      const employeeRef = collection(tenantRef, 'employees')

      const unsubscribe = onSnapshot(employeeRef, (snapshot) => {
        const employeeData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          end_date: doc.data().end_date
        })).filter((data) => !data.end_date)
        setEmployees(employeeData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting employees:', error)
    }
  }

  logActivity = async (currentUser, message, action, target) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const activitiesRef = collection(tenantRef, 'activities')

      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()
      const todaysDate = month + '/' + day + '/' + year

      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} ${message}`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: action,
        target: target,
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })
    } catch (error) {
      console.error('Error logging activity:', error)
    }
  }

  exportAssets = async (currentUser) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetRef = collection(tenantRef, 'assets')
      const querySnapshot = await getDocs(assetRef)

      const data = querySnapshot.docs.map((doc) => doc.data()).filter((doc) => doc.is_repo !== true)
      return data
    } catch (error) {
      console.error('Error exporting assets:', error)
      throw error
    }
  }

  exportRepositories = async (currentUser) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetRef = collection(tenantRef, 'assets')
      const querySnapshot = await getDocs(assetRef)

      const data = querySnapshot.docs.map((doc) => doc.data()).filter((doc) => doc.is_repo === true)
      return data
    } catch (error) {
      console.error('Error exporting repositories:', error)
      throw error
    }
  }

  getDomains = (currentUser, setDomains) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetRef = collection(tenantRef, 'assets')

      const unsubscribe = onSnapshot(assetRef, (snapshot) => {
        const domainData = snapshot.docs
          .map((item) => {
            const data = item.data()
            return {
              ...data,
              id: item.id,
              type: data.type,
            }
          })
          .filter((data) => data.type === 'domain')
        setDomains(domainData)
      })
      return unsubscribe
    } catch (error) {
      console.error('Error getting domains:', error)
    }
  }

  updateDomainAuditScope = async (domainIds, newScopeValue, currentUser) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)

      for (const domainId of domainIds) {
        const assetRef = doc(tenantRef, 'assets', domainId)
        await updateDoc(assetRef, {
          audit_scope: newScopeValue,
        })
      }

      await this.logActivity(
        currentUser, 
        `marked ${domainIds.length} domain(s) ${newScopeValue ? 'in' : 'out of'} audit scope`, 
        'Domain scope updated', 
        'Asset Management'
      )

      return true
    } catch (error) {
      console.error('Error updating domain audit scope:', error)
      throw error
    }
  }

  assignDomainOwner = async (domainIds, ownerName, currentUser) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)

      for (const domainId of domainIds) {
        const assetRef = doc(tenantRef, 'assets', domainId)
        await updateDoc(assetRef, {
          integration_owner: ownerName,
        })
      }
      await this.logActivity(
        currentUser, 
        `assigned owner '${ownerName}' to ${domainIds.length} domain(s)`, 
        'Domain owner assigned', 
        'Asset Management'
      )

      return true
    } catch (error) {
      console.error('Error assigning domain owner:', error)
      throw error
    }
  }

  exportDomains = async (currentUser) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetRef = collection(tenantRef, 'assets')
      const querySnapshot = await getDocs(assetRef)

      const data = querySnapshot.docs.map((doc) => doc.data()).filter((doc) => doc.type === 'domain')
      return data
    } catch (error) {
      console.error('Error exporting domains:', error)
      throw error
    }
  }
}

const assetDataService = new AssetDataService();
export default assetDataService;
